import React, { useState, useEffect } from "react";
import { FiX, FiActivity, FiCheck } from "react-icons/fi";
import toast from "react-hot-toast";
import { updateEventStatus } from "../../../api/eventApi";

const STATUS_OPTIONS = [
  {
    value: "running",
    label: "Running",
    badge: "🟢 Active",
    description: "Event is live. Users can participate, check in daily, and accumulate points.",
    cardClass: "border-emerald-200 hover:border-emerald-400 bg-emerald-50/40",
    activeCardClass: "border-emerald-500 bg-emerald-50/80 ring-2 ring-emerald-400",
    textClass: "text-emerald-700",
  },
  {
    value: "upcoming",
    label: "Upcoming",
    badge: "🟡 Scheduled",
    description: "Event is scheduled for a future date. Users can preview details before it begins.",
    cardClass: "border-amber-200 hover:border-amber-400 bg-amber-50/40",
    activeCardClass: "border-amber-500 bg-amber-50/80 ring-2 ring-amber-400",
    textClass: "text-amber-700",
  },
  {
    value: "completed",
    label: "Completed",
    badge: "🔵 Concluded",
    description: "Event is finalized. Points are locked and prizes ready for distribution.",
    cardClass: "border-blue-200 hover:border-blue-400 bg-blue-50/40",
    activeCardClass: "border-blue-500 bg-blue-50/80 ring-2 ring-blue-400",
    textClass: "text-blue-700",
  },
  {
    value: "noEvent",
    label: "No Event",
    badge: "⚪ Inactive / Draft",
    description: "Temporarily pause or hide event activity without deleting data.",
    cardClass: "border-gray-200 hover:border-gray-300 bg-gray-50/60",
    activeCardClass: "border-gray-500 bg-gray-100 ring-2 ring-gray-400",
    textClass: "text-gray-700",
  },
];

const EventStatusModal = ({ isOpen, onClose, onSuccess, event }) => {
  const [selectedStatus, setSelectedStatus] = useState("running");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && event) {
      setSelectedStatus(event.status || "running");
    }
  }, [isOpen, event]);

  if (!isOpen || !event) return null;

  const handleStatusChange = async () => {
    if (selectedStatus === event.status) {
      onClose();
      return;
    }

    setLoading(true);
    try {
      const idToUpdate = event.id || event.eventId;
      const res = await updateEventStatus(idToUpdate, selectedStatus);
      toast.success(res?.message || `Event status changed to '${selectedStatus}'`);
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.message || "Failed to update event status");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/80">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center font-bold">
              <FiActivity size={18} />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-800">Change Event Status</h3>
              <p className="text-xs text-gray-400 truncate max-w-[240px]">
                {event.eventName || event.eventId}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition"
          >
            <FiX size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-3">
          <p className="text-xs text-gray-500 mb-2">
            Select the new operational state for this event:
          </p>

          <div className="space-y-2.5">
            {STATUS_OPTIONS.map((opt) => {
              const isSelected = selectedStatus === opt.value;
              return (
                <div
                  key={opt.value}
                  onClick={() => setSelectedStatus(opt.value)}
                  className={`p-3.5 rounded-xl border cursor-pointer transition flex items-start gap-3 ${
                    isSelected ? opt.activeCardClass : opt.cardClass
                  }`}
                >
                  <div
                    className={`w-5 h-5 rounded-full border flex items-center justify-center mt-0.5 flex-shrink-0 ${
                      isSelected
                        ? "border-black bg-black text-white"
                        : "border-gray-300 bg-white"
                    }`}
                  >
                    {isSelected && <FiCheck size={12} />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-bold ${opt.textClass}`}>{opt.label}</span>
                      <span className="text-[11px] font-semibold">{opt.badge}</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                      {opt.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-xs font-semibold text-gray-600 bg-white border border-gray-200 hover:bg-gray-100 rounded-xl transition"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleStatusChange}
            disabled={loading}
            className="px-5 py-2 text-xs font-bold text-black bg-[#ffff00] hover:bg-[#e6e600] disabled:opacity-50 rounded-xl transition flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-3.5 h-3.5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                Updating...
              </>
            ) : (
              "Update Status"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EventStatusModal;
